import {
	DndContext,
	MouseSensor,
	TouchSensor,
	useDraggable,
	useDroppable,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getAnswerByQuizAndParticipant, postAnswer } from "../../api/answer";
import iconArrastrar from "../../assets/arrastrar.png";
import flechas from "../../assets/flechas.svg";
import LifeIndicatorBar from "../../components/LifeIndicatorBar";
import NextButton from "../../components/NextButton";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { updateLife, updateScore, updateTime } from "../../redux/actions";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getParticipantId,
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import {
	alertQuizAlreadyAnswered,
	modalCorrectIncorrectAnswer,
	modalQuestionInstructions,
} from "../../utils/logger";

function Pregunta7({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	const participant = useSelector((state) => state.group);

	const CORRECT_OPTION_POINTS = 25;
	const WRONG_OPTION_POINTS = 10;
	const PENALTY_SCORE_LIMIT = -100;

	const SCENARIO_ID = SCENARIOS.SCENARIO_7;
	const QUIZ_ID = QUIZZES.SCENARIO7_ESQUEMA;
	const NAVIGATE_TO = ROUTES.P7_SOLUTION;

	const [intentos, setIntentos] = useState(1);
	const [penaltyScore, setPenaltyScore] = useState(0);
	const [quizCompleted, setQuizCompleted] = useState(false);

	const touchSensor = useSensor(TouchSensor);
	const mouseSensor = useSensor(MouseSensor);

	const sensors = useSensors(touchSensor, mouseSensor);

	const MEJORA_ID = 1;
	const PRODUCCION_ID = 2;
	const EFICACIA_AGE_ID = 3;
	const AUMENTO_ID = 4;
	const EFICACIA_ALERGICA_ID = 5;
	const LIBERACION_ID = 6;
	const ACTIVACION_ID = 7;
	const INFLAMACION_ID = 8;
	const REDUCCION_ID = 9;

	const tagOptions = [
		{
			id: MEJORA_ID,
			value: "Mejora en la función pulmonar y reducción del remodelado",
		},
		{
			id: PRODUCCION_ID,
			value: "Producción de moco, aumento de su viscosidad y pérdida de la integridad epitelial",
		},
		{
			id: EFICACIA_AGE_ID,
			value: "Eficacia en pacientes con AGE y PN concomitante",
		},
		{
			id: AUMENTO_ID,
			value: "Aumento en la producción de moco e implicación en el remodelado tisular",
		},
		{
			id: EFICACIA_ALERGICA_ID,
			value: "Eficacia en pacientes con sensibilización alérgica",
		},
		{
			id: LIBERACION_ID,
			value: "Liberación de gránulos específicos con proteínas catiónicas",
		},
		{
			id: ACTIVACION_ID,
			value: "Activación de los inflamasomas de las células dendríticas y de los macrófagos que activan respuesta inmunitaria adaptativa",
		},
		{
			id: INFLAMACION_ID,
			value: "Inflamación de las vías respiratorias, remodelado tisular y daño tisular",
		},
		{
			id: REDUCCION_ID,
			value: "Reducción de exacerbaciones y uso de CO",
		},
	];

	const [tags, setTags] = useState(tagOptions);
	const OFFSET = tagOptions.length; //necessary for a proper behaviour of drag & drop library

	const [boxes, setBoxes] = useState([
		{
			id: MEJORA_ID + OFFSET,
			tag: null,
		},
		{
			id: PRODUCCION_ID + OFFSET,
			tag: null,
		},
		{ id: EFICACIA_AGE_ID + OFFSET, tag: null },
		{ id: AUMENTO_ID + OFFSET, tag: null },
		{ id: EFICACIA_ALERGICA_ID + OFFSET, tag: null },
		{ id: LIBERACION_ID + OFFSET, tag: null },
		{ id: ACTIVACION_ID + OFFSET, tag: null },
		{ id: INFLAMACION_ID + OFFSET, tag: null },
		{
			id: REDUCCION_ID + OFFSET,
			tag: null,
		},
	]);

	let instructionsHtml = `<div>
			<p class="txt16">
				¿Nos ayudas a completar este esquema sobre los efectos de
				mepolizumab al actuar sobre los mediadores de los eosinófilos?
				Arrastra una etiqueta hasta la ubicación que creas correcta.
			</p>
			<p class="txt16 text-red bold">MÚLTIPLES INTENTOS</p>
		</div>`;

	useEffect(() => {
		getAnswerByQuizAndParticipant(QUIZ_ID, getParticipantId())
			.then((res) => {
				if (res.status === 200 && res.data.length > 0) {
					alertQuizAlreadyAnswered({}).then((res) => {});
					setQuizCompleted(true);
				}
			})
			.catch((error) => {
				modalQuestionInstructions({
					instruccionsTextHtml: instructionsHtml,
					questionNum: getScenarioByScreen(location.pathname),
				});
			});
	}, [location]);

	const [time, setTime] = useState(0);

	useTimer(
		() => {
			setTime(time + 1);
		},
		quizCompleted ? null : 1000
	);

	async function submitAnswer({ correct, score, penalty }) {
		let totalTime = getTimer();
		dispatch(updateTime(totalTime));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: QUIZ_ID,
			participant: participant?._id,
			score: score,
			time: time,
			correct: correct,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateLife(res.data?.participant?.life));

					let penaltyScore = penalty || 0;

					dispatch(updateScore(score, SCORE_TYPES.SCORE));
					dispatch(
						updateScore(
							-Math.abs(penaltyScore),
							SCORE_TYPES.PENALTY
						)
					);

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + score,
						penalty: participant?.penalty - Math.abs(penaltyScore),
						lastVisited: NAVIGATE_TO,
					});
					setQuizCompleted(true);
				}
			})
			.catch((error) => {});
	}

	function checkAnswers() {
		let normalizedBoxes = boxes.map((x) => ({ ...x, id: x.id - OFFSET }));
		let correctOptions = normalizedBoxes.filter((x) => x.id === x.tag?.id);
		let wrongPlacedTags = normalizedBoxes.filter(
			(x) => x.tag && x.id !== x.tag?.id
		);
		let emptyBoxes = normalizedBoxes.filter((x) => !x.tag);

		let correct = wrongPlacedTags.length === 0 && emptyBoxes.length === 0;

		let newPenaltyScore =
			penaltyScore + wrongPlacedTags.length * -WRONG_OPTION_POINTS;

		let newScore =
			correctOptions.length * CORRECT_OPTION_POINTS + newPenaltyScore;

		setPenaltyScore(newPenaltyScore);

		let text =
			correct && intentos === 1
				? `<p class="txt16">
					Has obtenido el máximo de puntuación a la primera.
				</p>`
				: correct
				? `<div class="txt16">
					<p>Prueba superada.</p>					
				</div>`
				: `<div class="txt16">
					<p>
						Vaya, parece que has tenido algún problema para resolver
						esta prueba con éxito. Revisa la solución a
						continuación.
					</p>
					<p>
						Has obtenido
						<span class="text-red bold">
							${newScore} puntos
						</span>
					</p>
				</div>`;

		let submitAnswers = correct || newScore < PENALTY_SCORE_LIMIT;

		if (submitAnswers) {
			modalCorrectIncorrectAnswer({
				title: correct ? "¡Enhorabuena!" : "",
				textHtml: text,
				points: Math.max(newScore, PENALTY_SCORE_LIMIT),
				correct: correct,
				loosedPoints: correct ? null : newScore,
			}).then((res) => {
				if (res.isConfirmed) {
					navigate(NAVIGATE_TO);
				}
			});
			submitAnswer({
				correct: correct,
				score: Math.max(newScore, PENALTY_SCORE_LIMIT),
				penalty: Math.max(newPenaltyScore, PENALTY_SCORE_LIMIT),
			});
		}

		const correctBoxes = boxes.map((b) =>
			b.id - OFFSET === b?.tag?.id
				? {
						...b,
				  }
				: { ...b, tag: null }
		);

		setBoxes(correctBoxes);

		const wrongTags = tagOptions.filter(
			(tagOption) =>
				!correctBoxes.some((x) => x?.tag?.id === tagOption.id)
		);

		setTags(wrongTags);

		setIntentos(intentos + 1);
	}

	const handleDragEnd = ({ active, over }) => {
		window.scrollTo(0, 0);
		if (!active || !over) return;

		const draggedTag = tagOptions.find((x) => x.id === active?.id);
		const targetBox = boxes.find((x) => x.id === over?.id);

		const boxId = boxes.find((x) => x.tag?.id === draggedTag?.id)?.id;

		const updatedBoxes = boxes.map((b) =>
			b.id === targetBox?.id
				? {
						...b,
						tag: draggedTag,
				  }
				: b.id === boxId
				? { ...b, tag: null }
				: b
		);

		setBoxes(updatedBoxes);

		let updatedTags;

		if (over?.data?.current?.origin) {
			updatedTags = tagOptions.filter(
				(x) => tags.some((y) => y.id === x.id) || x.id === draggedTag.id
			);
		} else {
			updatedTags = tags.filter((x) => x.id !== draggedTag.id);
		}

		setTags(updatedTags);
	};

	const Draggable = (props) => {
		const { attributes, listeners, setNodeRef, transform } = useDraggable({
			id: props.id,
			disabled: false,
		});

		const style = {
			transform: CSS.Translate.toString(transform),
			lineHeight: "18px",
		};

		const isDropped = !tags.some((x) => x.id === props.id);

		return (
			<div
				className={`tag-box-memory p-1 ${
					isDropped ? "dropped w-100" : "ms-2"
				} txt11`}
				ref={setNodeRef}
				style={style}
				{...listeners}
				{...attributes}
			>
				{props.children}
			</div>
		);
	};

	const Droppable = (props) => {
		const { isOver, setNodeRef } = useDroppable({
			id: props.id,
			data: {
				classes: props.classes,
				origin: props.origin,
			},
		});

		const emptyTagListElem = props.origin;

		return (
			<>
				<div
					className={`tag-box-target-p7 p-1 txt11 ${
						emptyTagListElem ? "empty ms-2 bg-transparent" : ""
					} ${props.classes}`}
					ref={setNodeRef}
				>
					{props.children}
				</div>
			</>
		);
	};

	const TagBoxTarget = ({ boxId, classes }) => {
		const box = boxes.find((x) => x.id === boxId + OFFSET);
		const tag = tags.find((x) => x.id === boxId);

		return box?.tag === null ? (
			<Droppable id={box?.id} classes={classes}>
				{tag?.value}
			</Droppable>
		) : (
			<Draggable id={box?.tag?.id}>{box?.tag?.value}</Draggable>
		);
	};

	const TagBox = ({ tagId, classes }) => {
		const tag = tagOptions.find((x) => x.id === tagId);
		const isDropped = !tags.some((x) => x?.id == tag?.id);

		return !isDropped ? (
			<Draggable id={tag?.id}>{tag?.value}</Draggable>
		) : (
			<Droppable id={tag?.id} origin={true}>
				{tag?.value}
			</Droppable>
		);
	};

	return (
		<div className="pt-3 h-100 bg-pregunta-extra">
			<div className="container">
				<div className="row h-100 d-flex align-items-center">
					<div className="col-11 d-flex justify-content-center align-items-center">
						<DndContext
							onDragEnd={handleDragEnd}
							sensors={sensors}
							modifiers={[restrictToWindowEdges]}
						>
							<div className="w-100">
								<div className="d-flex align-items-center justify-content-end">
									<div className="bg-info-box txt12 p-3">
										Crecimiento, diferenciación,
										reclutamiento, activación y
										supervivencia de los eosinófilos
									</div>
									<div className="schema-line"></div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={LIBERACION_ID}
										></TagBoxTarget>
									</div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1 ms-2">
										<TagBoxTarget
											boxId={INFLAMACION_ID}
										></TagBoxTarget>
									</div>
									<div className="schema-line"></div>
									<div
										className="bg-info-box txt12 p-3 text-center"
										style={{
											minWidth: "90px",
											maxWidth: "90px",
										}}
									>
										Nucala®
									</div>
									<div className="schema-line"></div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={REDUCCION_ID}
										></TagBoxTarget>
									</div>
								</div>
								<div className="d-flex align-items-center justify-content-end mt-2">
									<div>
										<div className="bg-info-box txt12 px-3 py-2">
											Galectina-10
										</div>
										<div className="bg-info-box txt12 px-3 py-2 mt-1">
											CLC
										</div>
									</div>
									<div>
										<div className="schema-line inclined-down"></div>
										<div className="schema-line inclined-up mt-3"></div>
									</div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={ACTIVACION_ID}
										></TagBoxTarget>
									</div>
									<div className="schema-line"></div>
									<div
										className="bg-info-box txt12 p-3 text-center"
										style={{
											minWidth: "90px",
											maxWidth: "90px",
										}}
									>
										Nucala®
									</div>
									<div className="schema-line"></div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={EFICACIA_ALERGICA_ID}
										></TagBoxTarget>
									</div>
								</div>
								<div className="d-flex align-items-center justify-content-end mt-2">
									<div className="bg-info-box txt12 px-3 py-2">
										Redes de ADN
									</div>
									<div className="schema-line"></div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={AUMENTO_ID}
										></TagBoxTarget>
									</div>
									<div className="schema-line"></div>
									<div
										className="bg-info-box txt12 p-3 text-center"
										style={{
											minWidth: "90px",
											maxWidth: "90px",
										}}
									>
										Nucala®
									</div>
									<div className="schema-line"></div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={MEJORA_ID}
										></TagBoxTarget>
									</div>
								</div>
								<div className="d-flex align-items-center justify-content-end mt-2">
									<div>
										<div className="bg-info-box txt12 px-3 py-2">
											CLC
										</div>
										<div className="bg-info-box txt12 px-3 py-2 mt-1">
											Redes de ADN
										</div>
									</div>
									<div>
										<div className="schema-line inclined-down"></div>
										<div className="schema-line inclined-up mt-3"></div>
									</div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={PRODUCCION_ID}
										></TagBoxTarget>
									</div>
									<div className="schema-line"></div>
									<div
										className="bg-info-box txt12 p-3 text-center"
										style={{
											minWidth: "90px",
											maxWidth: "90px",
										}}
									>
										Nucala®
									</div>
									<div className="schema-line"></div>
									<div className="bg-tag-pink d-flex align-items-center justify-content-center p-1">
										<TagBoxTarget
											boxId={EFICACIA_AGE_ID}
										></TagBoxTarget>
									</div>
								</div>
								<div className="d-flex align-items-center justify-content-end mt-2">
									<TagBox tagId={MEJORA_ID}></TagBox>
									<TagBox tagId={PRODUCCION_ID}></TagBox>
									<TagBox tagId={EFICACIA_AGE_ID}></TagBox>
								</div>
								<div className="d-flex align-items-center justify-content-end mt-2">
									<TagBox tagId={AUMENTO_ID}></TagBox>
									<TagBox
										tagId={EFICACIA_ALERGICA_ID}
									></TagBox>
									<TagBox tagId={LIBERACION_ID}></TagBox>
								</div>
								<div className="d-flex align-items-center justify-content-end mt-2">
									<TagBox tagId={ACTIVACION_ID}></TagBox>
									<TagBox tagId={INFLAMACION_ID}></TagBox>
									<TagBox tagId={REDUCCION_ID}></TagBox>
								</div>
							</div>
						</DndContext>
					</div>
					<div className="col-1">
						<div className="d-flex flex-column align-items-end">
							<LifeIndicatorBar
								numIntentos={null}
							></LifeIndicatorBar>
						</div>
					</div>
				</div>
				<div
					className="container ps-0 pe-4"
					style={{ position: "absolute", bottom: "8vh" }}
				>
					<div className="row d-flex align-items-center">
						<div className="col-3 d-flex align-items-center txt12">
							<div className="bg-points-info me-1">
								+{CORRECT_OPTION_POINTS}
							</div>
							<div>puntos por etiqueta correcta</div>
						</div>
						<div className="col-3 d-flex align-items-center txt12">
							<div className="bg-wrong-points-info me-1">
								- {WRONG_OPTION_POINTS}
							</div>
							<div>
								puntos (hasta {PENALTY_SCORE_LIMIT} puntos)
							</div>
						</div>
						<div className="col-3 d-flex align-items-center txt12">
							<img
								src={iconArrastrar}
								alt="arrastra"
								width={37}
								className="me-1"
							></img>
							<div>
								Arrastra cada etiqueta a su lugar
								correspondiente.
							</div>
						</div>
						<div className="col-3 d-flex justify-content-end">
							{!quizCompleted ? (
								<button
									onClick={() => {
										checkAnswers();
									}}
									className="btn-iniciar p-2 px-4"
								>
									COMPROBAR
									<img className="ms-2" src={flechas}></img>
								</button>
							) : (
								<NextButton
									navigateTo={NAVIGATE_TO}
								></NextButton>
							)}
						</div>
					</div>
				</div>
			</div>
			<ScenarioBar
				scenarioNumber={getScenarioByScreen(location.pathname)}
			></ScenarioBar>
		</div>
	);
}

export default injectIntl(Pregunta7);
